import React, { useRef, useState } from "react";
import {
  Drawer,
  Button,
  CheckPicker,
  DateRangePicker,
  Toggle,
  IconButton,
  Tag,
  Checkbox,
} from "rsuite";
import trashIcon from "../../assets/img/trash.svg";
import infoCircleIcon from "../../assets/img/stop-sell-info.svg";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import { getApiErrorMessage } from "../../utils/apiErrorMessage";
import { useSelector } from "react-redux";
import { beforeToday } from "rsuite/cjs/DateRangePicker/disabledDateUtils";
import { updateARI } from "../../api";
import { UAL_SOURCES } from "../../constants/ualActions";
import { usePropertyStore } from "../../contexts/PropertyStoreContext";
import {
  selectNegotiatedRateplans,
  selectRateplans,
} from "../../slices/rateplanSlice";
import { selectRoomtypes } from "../../slices/roomtypeSlice";
import {
  formatDateRange,
  disableDatesBeyondMaxRange,
} from "../../utils/dateUtils";
import { buildStopSellPayload } from "./stopSellUtils";
import "./stop-sell.scss";

const StopSellPanel = ({ open, onClose, isOffline }) => {
  const { selectedProperty } = usePropertyStore();
  const negotiatedRateplans = useSelector(selectNegotiatedRateplans);
  const allRateplans = useSelector(selectRateplans);
  const rateplans = isOffline ? allRateplans : negotiatedRateplans;
  const rooms = useSelector(selectRoomtypes);

  const [selectedRateplans, setSelectedRateplans] = useState([]);
  const [selectedRoomTypes, setSelectedRoomTypes] = useState([]);
  const [dateRanges, setDateRanges] = useState([{ id: 0, value: [] }]);
  const [availability, setAvailability] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const dateRangeIdRef = useRef(1);

  const shouldDisableDate = disableDatesBeyondMaxRange(2, beforeToday());

  const rateplansData = rateplans.map((rp) => ({
    label: rp.code && rp.name ? `${rp.code}-${rp.name}` : rp.name || rp.code,
    value: rp.id,
  }));

  const roomsData = rooms.map((room) => ({
    label: room.displayName,
    value: room.id,
  }));

  const handleAddDateRange = () => {
    const newId = dateRangeIdRef.current++;
    setDateRanges([...dateRanges, { id: newId, value: [] }]);
  };

  const handleRemoveDateRange = (id) => {
    if (dateRanges.length <= 1) return;
    setDateRanges(dateRanges.filter((dr) => dr.id !== id));
  };

  const handleDateRangeChange = (id, value) => {
    setDateRanges(
      dateRanges.map((dr) =>
        dr.id === id ? { ...dr, value: value || [] } : dr
      )
    );
  };

  const handleReset = () => {
    setSelectedRateplans([]);
    setSelectedRoomTypes([]);
    setDateRanges([{ id: 0, value: [] }]);
    setAvailability(true);
    dateRangeIdRef.current = 1;
  };

  const handleApply = async () => {
    if (selectedRateplans.length === 0) {
      toast.warn("Please select at least one rate plan.");
      return;
    }
    if (selectedRoomTypes.length === 0) {
      toast.warn("Please select at least one room type.");
      return;
    }
    const validDateRanges = dateRanges
      .map((dr) => dr.value)
      .filter((v) => v.length === 2);
    if (validDateRanges.length === 0) {
      toast.warn("Please select at least one date range.");
      return;
    }

    setIsLoading(true);
    try {
      const payload = buildStopSellPayload({
        propertyId: selectedProperty.propertyId,
        rateplanIds: selectedRateplans,
        roomtypeIds: selectedRoomTypes,
        dateRanges: validDateRanges,
        isOpen: availability,
      });
      await updateARI(payload, UAL_SOURCES.ARI_STOP_SELL);
      toast.success("Stop Sell applied successfully.");
      handleReset();
      onClose();
    } catch (error) {
      toast.error(
        getApiErrorMessage(
          error,
          "Failed to apply Stop Sell. Please try again."
        )
      );
    } finally {
      setIsLoading(false);
    }
  };

  const renderRateplanValue = (values) => {
    if (values.length === 0) return null;
    const first = rateplansData.find((rp) => rp.value === values[0]);
    if (values.length === 1) return <Tag>{first?.label}</Tag>;
    return (
      <>
        <Tag>{first?.label}</Tag>
        <Tag className="stop-sell-tag-count">+{values.length - 1}</Tag>
      </>
    );
  };

  const renderRoomValue = (values) => {
    if (values.length === 0) return null;
    const first = roomsData.find((r) => r.value === values[0]);
    if (values.length === 1) return <Tag>{first?.label}</Tag>;
    return (
      <>
        <Tag>{first?.label}</Tag>
        <Tag className="stop-sell-tag-count">+{values.length - 1}</Tag>
      </>
    );
  };

  const allRoomCheckBox = (
    <div className="rs-picker-list-item" tabIndex="-1">
      <Checkbox
        checked={selectedRoomTypes.length === rooms.length}
        onChange={(_, checked) => {
          setSelectedRoomTypes(checked ? rooms.map((r) => r.id) : []);
        }}
        className="rs-check-item all-room-checkbox"
      >
        All Room Types
      </Checkbox>
    </div>
  );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="right"
      size="sm"
      className="stop-sell-drawer"
    >
      <Drawer.Header>
        <Drawer.Title>Stop Sell</Drawer.Title>
      </Drawer.Header>
      <Drawer.Body>
        <p className="stop-sell-subtitle">
          Mark multiple Rate Plans, Room Types, and date ranges as non-bookable.
        </p>

        {!isOffline && (
          <div className="stop-sell-info-banner">
            <img
              src={infoCircleIcon}
              alt="Info"
              width="20"
              height="20"
              className="stop-sell-info-icon"
            />
            <span>
              Only editable static Rate Plans are shown below. To update any
              other Rate Plans, please make changes in your CRS.
            </span>
          </div>
        )}

        <div className="stop-sell-card">
          <div className="stop-sell-field">
            <span className="stop-sell-label">Property</span>
            <span className="stop-sell-property-name">
              {selectedProperty?.propertyName}
            </span>
          </div>

          <div className="stop-sell-select-section">
            <div className="stop-sell-section-label">Select</div>

            <div className="stop-sell-field">
              <CheckPicker
                data={rateplansData}
                value={selectedRateplans}
                onChange={setSelectedRateplans}
                placeholder="All Rate Plans"
                block
                searchable
                renderValue={renderRateplanValue}
                data-testid="stop-sell-rateplan-picker"
              />
            </div>

            <div className="stop-sell-field">
              <CheckPicker
                data={roomsData}
                value={selectedRoomTypes}
                onChange={setSelectedRoomTypes}
                placeholder="All Room Types"
                block
                searchable
                renderValue={renderRoomValue}
                renderMenu={(menu) => (
                  <>
                    {!(
                      React.isValidElement(menu) &&
                      menu.props?.children === "No results found"
                    ) && (
                      <>
                        {allRoomCheckBox}
                        {menu}
                      </>
                    )}
                  </>
                )}
                data-testid="stop-sell-roomtype-picker"
              />
            </div>

            {dateRanges.map((dr) => (
              <div className="stop-sell-date-row" key={dr.id}>
                <DateRangePicker
                  value={dr.value.length === 2 ? dr.value : null}
                  onChange={(value) => handleDateRangeChange(dr.id, value)}
                  shouldDisableDate={shouldDisableDate}
                  placeholder="Select date range"
                  format="dd-MM-yyyy"
                  renderValue={formatDateRange}
                  editable={false}
                  block
                  ranges={[]}
                  placement="autoVerticalEnd"
                />
                <IconButton
                  icon={
                    <img src={trashIcon} alt="Delete" width="20" height="20" />
                  }
                  appearance="subtle"
                  size="sm"
                  onClick={() => handleRemoveDateRange(dr.id)}
                  className="stop-sell-delete-date"
                  disabled={dateRanges.length <= 1}
                />
              </div>
            ))}

            <Button
              appearance="link"
              className="stop-sell-add-date"
              onClick={handleAddDateRange}
            >
              Add another date range
            </Button>
          </div>

          <div className="stop-sell-availability">
            <span className="stop-sell-label">Availability</span>
            <div className="stop-sell-toggle-row">
              <Toggle
                checked={availability}
                onChange={setAvailability}
                locale={{ on: "Open", off: "Closed" }}
                size="md"
              >
                {availability ? "Open" : "Closed"}
              </Toggle>
            </div>
          </div>

          <Button
            appearance="primary"
            block
            loading={isLoading}
            onClick={handleApply}
            className="stop-sell-apply-btn"
          >
            Apply Stop Sell
          </Button>
        </div>
      </Drawer.Body>
    </Drawer>
  );
};

StopSellPanel.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  isOffline: PropTypes.bool,
};

export default StopSellPanel;
